import React, {useEffect, useState} from 'react';
import {
  Dialog,
  DialogContent,
  Link,
  Button,
  Checkbox,
  DialogFooter,
} from '@taopix/taopix-design-system';
import {useTranslation} from 'react-i18next';
import {AppActions} from '../../Context/AppActionTypes';
import {useUserFlowState} from '../../Context/UserFlow/UserFlowContext';
import {UserFlowStep} from '../../../Facade/UserFlow/BaseUserFlow';
import {useSelector} from 'react-redux';
import {FlightCheckModel} from '@taopix/taopix-core/dist/types/Reducers/FlightCheckReducer';
import {UserFlowType} from '../../../Facade/UserFlow/UserFlowFacade';
import {useTaopix} from '../../Context/TaopixContext';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {ViewMode} from '@taopix/taopix-core';
import {useExperience} from '../../Experience/ExperienceProvider';
import useViewMode from '../../../Hooks/useViewMode';

export type FlightCheckReviewDialogProps = {
  isOpen: boolean;
};

const FlightCheckReviewDialog = ({isOpen}: FlightCheckReviewDialogProps) => {
  const {t} = useTranslation(['toolpalette', 'flightcheck']);
  const ui = useUiState();
  const userFlow = useUserFlowState();
  const app = useTaopix();
  const [viewMode, setViewMode] = useViewMode();

  const {experience} = useExperience();

  const [reviewAcceptChecked, setReviewAndAcceptChecked] = useState<boolean>(false);

  useEffect(() => {
    if (reviewAcceptChecked) {
      setReviewAndAcceptChecked(false);
    }
  }, [isOpen]);

  const errors: FlightCheckModel = useSelector<any>(state => state.flightcheck) as any;

  const onReviewIssues = () => {
    app.dispatch(AppActions.setUserFlowStage(UserFlowType.UserFlow));
    userFlow.setCurrentStep(`${UserFlowStep.FlightCheck}.${UserFlowStep.FlightCheckIssueFix}`);
    // if in preview view send user back to the default in order to view flightcheck palette
    if (viewMode === ViewMode.Preview) {
      switch (experience.EDITOR.workArea.defaultView) {
        case 'gv':
          setViewMode(ViewMode.Grid);
          break;
        case 'ls':
          setViewMode(ViewMode.List);
          break;
        case 'spv':
          setViewMode(ViewMode.Single);
          break;
        default:
          setViewMode(ViewMode.List);
          break;
      }
    }
  };

  // if there are any critical errors then show critical error dialog
  if (Object.keys(errors?.criticalErrors).length > 0) {
    return (
      <Dialog
        id={'critical-dialog'}
        open={isOpen}
        width={500}
        theme={'tpx-theme-dialogs'}
        themeTip="Dialogs"
        title={t('flightcheck:str_MessagePleaseReviewProject')}
        aria-live="assertive"
      >
        <DialogContent>
          <p>{t('flightcheck:str_MessageIssuesInProjectCritical')}</p>
        </DialogContent>
        <DialogFooter isSmallScreen={ui.state.isSmallScreen}>
          <Link
            className={'lg:mr-auto'}
            onClick={() => {
              userFlow.setCurrentStep(`${UserFlowType.FlightCheck}.${UserFlowStep.None}`);
            }}
          >
            {t('*:str_ButtonCancel')}
          </Link>
          <Button
            buttonStyle={'primary'}
            size={'small'}
            label={t('flightcheck:str_LabelReviewIssues')}
            onClick={onReviewIssues}
          />
        </DialogFooter>
      </Dialog>
    );
  } else {
    return (
      <Dialog
        id={'warning-dialog'}
        open={isOpen}
        width={ui.state.isSmallScreen ? 400 : 500}
        title={t('flightcheck:str_MessagePleaseReviewProject')}
        aria-live="assertive"
      >
        <DialogContent>
          <div className={'mb-md'}>
            <p>{t('flightcheck:str_MessageIssuesInProjectWarning')}</p>
          </div>
          <Checkbox
            id={`accept-issues-checkbox${reviewAcceptChecked}`}
            label={t('flightcheck:str_MessageReviewedAndAcceptIssues')}
            onChange={(isChecked: boolean) => {
              setReviewAndAcceptChecked(isChecked);
            }}
          />
        </DialogContent>
        <DialogFooter isSmallScreen={ui.state.isSmallScreen}>
          <Button
            buttonStyle={ui.state.isSmallScreen ? 'link' : 'negative'}
            onClick={() => {
              userFlow.setCurrentStep(`${UserFlowType.FlightCheck}.${UserFlowStep.None}`);
            }}
          >
            {t('*:str_ButtonCancel')}
          </Button>
          <Button
            id={'button-continue-anyway'}
            buttonStyle={'secondary'}
            label={t('flightcheck:str_LabelContinueAnyway')}
            onClick={() => {
              userFlow.setNextStep();
            }}
            disabled={!reviewAcceptChecked}
          />
          <Button
            buttonStyle={'primary'}
            label={t('flightcheck:str_LabelReviewIssues')}
            onClick={onReviewIssues}
          />
        </DialogFooter>
      </Dialog>
    );
  }
};

export default FlightCheckReviewDialog;
